import {useAtom, useAtomValue, useSetAtom} from 'jotai';
import {useEffect} from 'react';
import {
    notificationsAtom,
    unreadCountAtom,
    processingNotificationsAtom,
    addNotificationAtom,
    updateNotificationAtom,
    markAsReadAtom,
    markAllAsReadAtom,
    removeNotificationAtom,
    clearOldNotificationsAtom,
} from '@/store/notifications';
import {
    loadNotifications,
    saveNotifications,
    subscribeToStorageChanges,
} from '@/util/notification-storage';

/**
 * Main hook for managing notifications
 * Handles localStorage persistence and provides easy-to-use methods
 */
export function useNotifications() {
    const [notifications, setNotifications] = useAtom(notificationsAtom);
    const unreadCount = useAtomValue(unreadCountAtom);
    const processingNotifications = useAtomValue(processingNotificationsAtom);

    const addNotification = useSetAtom(addNotificationAtom);
    const updateNotification = useSetAtom(updateNotificationAtom);
    const markAsRead = useSetAtom(markAsReadAtom);
    const markAllAsRead = useSetAtom(markAllAsReadAtom);
    const removeNotification = useSetAtom(removeNotificationAtom);
    const clearOldNotifications = useSetAtom(clearOldNotificationsAtom);

    // Load notifications from localStorage on mount
    useEffect(() => {
        const stored = loadNotifications();
        if (stored.length > 0) {
            setNotifications(stored);
        }

        // Subscribe to storage changes from other tabs
        const unsubscribe = subscribeToStorageChanges(
            (updatedNotifications) => {
                setNotifications(updatedNotifications);
            }
        );

        return unsubscribe;
    }, [setNotifications]);

    // Save notifications to localStorage whenever they change
    useEffect(() => {
        if (notifications.length > 0 || loadNotifications().length > 0) {
            saveNotifications(notifications);
        }
    }, [notifications]);

    return {
        // State
        notifications,
        unreadCount,
        processingNotifications,

        // Actions
        addNotification,
        updateNotification,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearOldNotifications,
    };
}
